import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/LangContext";
import { BUSINESS, REVIEWS } from "@/lib/business";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Logo from "@/components/Logo";
import {
  ArrowUpRight,
  Smartphone,
  Tablet,
  Monitor,
  Gamepad2,
  Home,
  Store,
  Wrench,
  CheckCircle2,
  Clock,
  Phone,
  MapPin,
  Mail,
  ShieldCheck,
  Star,
  Search,
  Cpu,
  Package,
} from "lucide-react";

const TECH_IMG =
  "https://images.pexels.com/photos/6754839/pexels-photo-6754839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const COMP_IMG =
  "https://images.unsplash.com/photo-1721332154191-ba5f1534266e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxtb3RoZXJib2FyZCUyMHJlcGFpcnxlbnwwfHx8fDE3ODU3ODA1MzZ8MA&ixlib=rb-4.1.0&q=85";

function ServiceCard({ icon: Icon, title, desc, testid }) {
  return (
    <div
      data-testid={testid}
      className="group p-8 hairline bg-white hover:bg-[#3D1015] hover:text-white transition-colors duration-300"
    >
      <div className="flex items-start justify-between">
        <Icon size={28} strokeWidth={1.5} />
        <ArrowUpRight
          size={18}
          className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all"
        />
      </div>
      <h3 className="font-display text-2xl tracking-tight mt-10">{title}</h3>
      <p className="text-sm mt-3 leading-relaxed opacity-80">{desc}</p>
    </div>
  );
}

function Stars({ n }) {
  return (
    <div className="flex gap-0.5 text-[#D9333F]">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
      ))}
    </div>
  );
}

export default function Landing() {
  const { t, lang } = useLang();
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.address.maps_query)}`;

  return (
    <div className="min-h-screen bg-[#F7F3F4] text-[#1A1214]">
      <Header active="home" />

      {/* HERO — deep maroon with circuit background echoing the business card */}
      <section className="relative overflow-hidden circuit-bg text-white">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 relative">
          <div className="lg:col-span-7 flex flex-col justify-end">
            <div className="tick-label !text-[#D9333F]" data-testid="hero-eyebrow">
              {lang === "es"
                ? "Reparación experta · microsoldadura · Lauderhill FL"
                : "Expert repair · microsoldering · Lauderhill FL"}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter mt-6">
              {t.hero.title_1}
              <br />
              <span className="text-[#D9333F]">{t.hero.title_2}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mt-8 max-w-xl leading-relaxed">
              {lang === "es"
                ? "Celulares, tablets, computadoras y consolas. Especialistas en microsoldadura. Reparación a domicilio o en el taller en Lauderhill. Venta al mayor y detal."
                : "Phones, tablets, computers, and consoles. Microsoldering specialists. Repairs at home or at our Lauderhill shop. Wholesale and retail."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="hero-cta-book"
                className="btn-accent"
              >
                {t.hero.cta_book} <ArrowUpRight size={18} />
              </Link>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="px-6 py-3.5 border border-white/30 text-white hover:bg-white hover:text-[#3D1015] transition-colors font-semibold text-sm"
              >
                {t.hero.cta_services}
              </a>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg">
              <div>
                <div className="font-display text-3xl">4+</div>
                <div className="tick-label mt-1 !text-white/60">{lang === "es" ? "Categorías" : "Categories"}</div>
              </div>
              <div>
                <div className="font-display text-3xl">48h</div>
                <div className="tick-label mt-1 !text-white/60">{lang === "es" ? "Entrega prom." : "Avg. delivery"}</div>
              </div>
              <div>
                <div className="font-display text-3xl">{BUSINESS.warranty_days}d</div>
                <div className="tick-label mt-1 !text-white/60">{lang === "es" ? "Garantía" : "Warranty"}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative flex items-center justify-center">
            {/* Big logo panel */}
            <div className="relative w-full max-w-[420px] aspect-square hairline-strong bg-[#1F1417] p-8 flex items-center justify-center">
              <Logo size={280} variant="dark" />
              <div className="absolute top-4 left-4 tick-label !text-white/50">CRC · EST.</div>
              <div className="absolute bottom-4 right-4 tick-label !text-[#D9333F]">MICROSOLDERING</div>
            </div>
            <div className="absolute -bottom-6 -left-6 md:-left-10 bg-white text-[#1A1214] hairline p-4 flex items-center gap-3" data-testid="owner-badge">
              <CheckCircle2 size={18} className="text-[#6B1F27]" />
              <div>
                <div className="tick-label">{lang === "es" ? "Ing. Certificado" : "Certified"}</div>
                <div className="font-display text-sm mt-1">{BUSINESS.owner.name}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Brand marks marquee */}
        <div className="border-t border-white/10 bg-black/20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center gap-x-10 gap-y-2 tick-label !text-white/60" translate="no">
            {BUSINESS.brand_marks.map((m, i) => (
              <React.Fragment key={m}>
                <span className="notranslate" translate="no">{m}</span>
                {i < BUSINESS.brand_marks.length - 1 && <span>·</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="trust-bar">
        <div className="hairline bg-white p-6 flex items-start gap-4">
          <ShieldCheck size={22} className="text-[#6B1F27] mt-1" />
          <div>
            <div className="tick-label">{lang === "es" ? "Garantía" : "Warranty"}</div>
            <div className="font-display text-lg tracking-tight mt-1">
              {BUSINESS.warranty_days} {lang === "es" ? "días" : "days"}
            </div>
          </div>
        </div>
        <div className="hairline bg-white p-6 flex items-start gap-4">
          <Cpu size={22} className="text-[#6B1F27] mt-1" />
          <div>
            <div className="tick-label">{lang === "es" ? "Microsoldadura" : "Microsoldering"}</div>
            <div className="font-display text-lg tracking-tight mt-1">
              {lang === "es" ? "A nivel de placa" : "Board-level"}
            </div>
          </div>
        </div>
        <div className="hairline bg-white p-6 flex items-start gap-4">
          <Package size={22} className="text-[#6B1F27] mt-1" />
          <div>
            <div className="tick-label">{lang === "es" ? "Venta" : "Sales"}</div>
            <div className="font-display text-lg tracking-tight mt-1">
              {lang === "es" ? "Mayor y detal" : "Wholesale · retail"}
            </div>
          </div>
        </div>
        <div className="hairline bg-white p-6 flex items-start gap-4">
          <MapPin size={22} className="text-[#6B1F27] mt-1" />
          <div>
            <div className="tick-label">{lang === "es" ? "Cobertura" : "Coverage"}</div>
            <div className="font-display text-lg tracking-tight mt-1">
              {BUSINESS.coverage_radius_mi} mi · {BUSINESS.address.city}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="tick-label">{t.services.label}</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-4">
              {t.services.title}
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ServiceCard testid="service-card-phone" icon={Smartphone} title={t.services.phone} desc={t.services.phone_desc} />
            <ServiceCard testid="service-card-tablet" icon={Tablet} title={t.services.tablet} desc={t.services.tablet_desc} />
            <ServiceCard testid="service-card-computer" icon={Monitor} title={t.services.computer} desc={t.services.computer_desc} />
            <ServiceCard testid="service-card-console" icon={Gamepad2} title={t.services.console} desc={t.services.console_desc} />
          </div>
        </div>
      </section>

      {/* SERVICE TYPES BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-7 hairline circuit-bg text-white p-10 flex flex-col justify-between min-h-[380px] relative" data-testid="tile-at-home">
            <div className="relative z-10">
              <div className="tick-label !text-[#D9333F]">{lang === "es" ? "Modalidad" : "Modality"}</div>
              <h3 className="font-display text-4xl md:text-5xl tracking-tighter mt-4">
                {lang === "es" ? "A domicilio" : "At home"}
              </h3>
              <p className="text-white/80 mt-6 max-w-md">
                {lang === "es"
                  ? `Nuestro técnico va donde estés dentro de ${BUSINESS.coverage_radius_mi} millas de ${BUSINESS.address.city}, ${BUSINESS.address.state}.`
                  : `Our tech comes to you within ${BUSINESS.coverage_radius_mi} miles of ${BUSINESS.address.city}, ${BUSINESS.address.state}.`}
              </p>
            </div>
            <div className="flex items-center gap-3 relative z-10">
              <Home size={20} />
              <span className="tick-label !text-white/60">
                {BUSINESS.coverage_radius_mi} mi / {BUSINESS.coverage_radius_km} km
              </span>
            </div>
          </div>
          <div className="md:col-span-5 hairline bg-white p-10 flex flex-col justify-between min-h-[380px]" data-testid="tile-in-shop">
            <div>
              <div className="tick-label">{lang === "es" ? "Modalidad" : "Modality"}</div>
              <h3 className="font-display text-4xl md:text-5xl tracking-tighter mt-4">
                {lang === "es" ? "En sitio" : "In shop"}
              </h3>
              <p className="text-neutral-600 mt-6">
                {lang === "es"
                  ? "Trae tu dispositivo al taller. Diagnóstico y presupuesto sin costo."
                  : "Drop your device off. Free diagnosis and quote."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Store size={20} className="text-[#6B1F27]" />
              <span className="tick-label">{BUSINESS.address.street}</span>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="tick-label">{t.process.label}</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-4 max-w-3xl">
          {t.process.title}
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { n: "01", icon: Clock, title: t.process.step1, desc: t.process.step1_desc },
            { n: "02", icon: Wrench, title: t.process.step2, desc: t.process.step2_desc },
            { n: "03", icon: CheckCircle2, title: t.process.step3, desc: t.process.step3_desc },
          ].map((s, i) => (
            <div key={i} className="hairline bg-white p-8" data-testid={`process-step-${i + 1}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono-tight text-[#D9333F]">{s.n}</span>
                <s.icon size={22} strokeWidth={1.5} />
              </div>
              <h4 className="font-display text-2xl tracking-tight mt-8">{s.title}</h4>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="max-w-[1400px] mx-auto px-6 md:px-10 py-16" data-testid="reviews-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="tick-label">{lang === "es" ? "Reseñas" : "Reviews"}</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-4">
              {lang === "es" ? "Lo que dicen nuestros clientes." : "What our customers say."}
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <Stars n={5} />
              <span className="text-sm text-neutral-600">
                {lang === "es" ? "5.0 promedio · clientes reales" : "5.0 avg · real customers"}
              </span>
            </div>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {REVIEWS.map((r, i) => (
              <div key={i} className="hairline bg-white p-6 flex flex-col justify-between" data-testid={`review-${i}`}>
                <div>
                  <Stars n={r.stars} />
                  <p className="text-sm mt-4 leading-relaxed">"{r.text[lang]}"</p>
                </div>
                <div className="mt-6">
                  <div className="font-display text-base tracking-tight">{r.name}</div>
                  <div className="tick-label mt-1">{r.device}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="aspect-[4/3] overflow-hidden hairline">
            <img src={TECH_IMG} alt="Technician" className="w-full h-full object-cover" />
          </div>
          <div className="aspect-[4/3] overflow-hidden hairline">
            <img src={COMP_IMG} alt="Motherboard repair" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CONTACT + COVERAGE */}
      <section id="contact" className="max-w-[1400px] mx-auto px-6 md:px-10 py-16" data-testid="contact-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="tick-label">{lang === "es" ? "Contacto" : "Contact"}</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-4">
              {lang === "es" ? "Hablemos." : "Let's talk."}
            </h2>
            <p className="text-neutral-600 mt-4 max-w-md">
              {lang === "es"
                ? "Llámanos, escríbenos por WhatsApp o visita nuestro taller. Diagnóstico gratuito."
                : "Call us, WhatsApp us, or visit the workshop. Free diagnosis."}
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={`tel:${BUSINESS.phone_raw}`}
                data-testid="contact-phone"
                className="hairline bg-white p-5 flex items-center gap-4 hover:bg-[#3D1015] hover:text-white transition-colors group"
              >
                <div className="w-10 h-10 grid place-items-center bg-[#F7F3F4] group-hover:bg-white/10 transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="tick-label">{lang === "es" ? "Teléfono / WhatsApp" : "Phone / WhatsApp"}</div>
                  <div className="font-mono-tight text-lg mt-1">{BUSINESS.phone_display}</div>
                </div>
              </a>
              <a
                href={`mailto:${BUSINESS.email}`}
                data-testid="contact-email"
                className="hairline bg-white p-5 flex items-center gap-4 hover:bg-[#3D1015] hover:text-white transition-colors group"
              >
                <div className="w-10 h-10 grid place-items-center bg-[#F7F3F4] group-hover:bg-white/10 transition-colors">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="tick-label">Email</div>
                  <div className="font-mono-tight text-base mt-1">{BUSINESS.email}</div>
                </div>
              </a>
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address"
                className="hairline bg-white p-5 flex items-center gap-4 hover:bg-[#3D1015] hover:text-white transition-colors group"
              >
                <div className="w-10 h-10 grid place-items-center bg-[#F7F3F4] group-hover:bg-white/10 transition-colors">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="tick-label">{lang === "es" ? "Dirección" : "Address"}</div>
                  <div className="text-base mt-1">
                    {BUSINESS.address.street}, {BUSINESS.address.city}, {BUSINESS.address.state} {BUSINESS.address.zip}
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="hairline bg-white p-6">
              <div className="tick-label mb-3">{lang === "es" ? "Horario de atención" : "Business hours"}</div>
              <ul className="divide-y divide-[#6B1F27]/10" data-testid="contact-hours">
                {BUSINESS.hours[lang].map((h) => (
                  <li key={h.days} className="py-3 flex justify-between items-center">
                    <span className="font-semibold">{h.days}</span>
                    <span className="font-mono-tight text-sm text-neutral-600">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="hairline mt-4 overflow-hidden bg-white" data-testid="coverage-map">
              <iframe
                title="map"
                className="w-full h-[280px] border-0 grayscale contrast-125"
                src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.address.maps_query)}&output=embed&z=11`}
                loading="lazy"
              />
              <div className="p-5 border-t border-[#6B1F27]/10 flex items-start gap-3">
                <MapPin size={16} className="text-[#6B1F27] mt-1" />
                <div className="text-sm text-neutral-600">
                  {lang === "es"
                    ? `Servicio a domicilio disponible en un radio de ${BUSINESS.coverage_radius_mi} millas (${BUSINESS.coverage_radius_km} km) desde ${BUSINESS.address.city}, ${BUSINESS.address.state}.`
                    : `At-home service within ${BUSINESS.coverage_radius_mi} miles (${BUSINESS.coverage_radius_km} km) of ${BUSINESS.address.city}, ${BUSINESS.address.state}.`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="hairline circuit-bg text-white p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative">
          <div className="relative z-10">
            <div className="tick-label !text-[#D9333F]">{lang === "es" ? "Empieza" : "Start"}</div>
            <h3 className="font-display text-4xl md:text-5xl tracking-tighter mt-4 max-w-xl">
              {lang === "es" ? "¿Listo para reparar?" : "Ready to fix it?"}
            </h3>
          </div>
          <Link
            to="/book"
            data-testid="footer-cta-book"
            className="bg-[#D9333F] hover:bg-[#B01B27] text-white px-8 py-4 font-semibold transition-colors inline-flex items-center gap-2 relative z-10"
          >
            {t.hero.cta_book} <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
